import Dexie, { type EntityTable } from "dexie";
import type { Bootstrap, DesktopState, EncryptedRecord } from "../types";

export type LocalRecord = EncryptedRecord & {
  dirty: number;
  baseRevision: number;
};
type MetaRow = { key: string; value: unknown };

class VaultDatabase extends Dexie {
  records!: EntityTable<LocalRecord, "id">;
  meta!: EntityTable<MetaRow, "key">;

  constructor() {
    super("nkriz-encrypted-vault");
    this.version(1).stores({
      records: "id, revision, dirty, deleted",
      meta: "key"
    });
  }
}

export const localDb = new VaultDatabase();

export async function cacheBootstrap(bootstrap: Bootstrap): Promise<void> {
  await localDb.meta.put({ key: "bootstrap", value: bootstrap });
}

export async function getCachedBootstrap(): Promise<Bootstrap | undefined> {
  return (await localDb.meta.get("bootstrap"))?.value as Bootstrap | undefined;
}

export async function getCursor(): Promise<number> {
  return ((await localDb.meta.get("cursor"))?.value as number | undefined) ?? 0;
}

export async function setCursor(cursor: number): Promise<void> {
  await localDb.meta.put({ key: "cursor", value: cursor });
}

export async function getLocalDesktopState(): Promise<DesktopState | undefined> {
  return (await localDb.meta.get("desktopState"))?.value as DesktopState | undefined;
}

export async function setLocalDesktopState(desktopState: DesktopState): Promise<void> {
  await localDb.meta.put({ key: "desktopState", value: desktopState });
}

export async function getDeviceId(): Promise<string> {
  const existing = (await localDb.meta.get("deviceId"))?.value;
  if (typeof existing === "string") return existing;
  const deviceId = crypto.randomUUID();
  await localDb.meta.put({ key: "deviceId", value: deviceId });
  return deviceId;
}

export async function getLeaseGeneration(): Promise<number | undefined> {
  const value = (await localDb.meta.get("leaseGeneration"))?.value;
  return typeof value === "number" ? value : undefined;
}

export async function setLeaseGeneration(generation: number): Promise<void> {
  await localDb.meta.put({ key: "leaseGeneration", value: generation });
}

export async function discardDirtyRecords(): Promise<number> {
  return localDb.transaction("rw", localDb.records, localDb.meta, async () => {
    const count = await localDb.records.where("dirty").equals(1).count();
    await localDb.records.where("dirty").equals(1).delete();
    await localDb.meta.put({ key: "cursor", value: 0 });
    return count;
  });
}
